import type { BoardContent } from './doc';
import { type BoardElement, createFrame, type Frame, MAX_FRAMES, normalizeFrame } from './elements';
import { base64ToBytes, bytesToBase64 } from './points';
import { sanitizeElement } from './sanitize';

/*
 * The .dvb file DoriavBoard exports and imports: a portable copy of a board with
 * its images embedded, so it can be moved between servers or kept as a backup.
 * Inside it is JSON.
 */

export const BOARD_FILE_FORMAT = 'doriavboard';
export const BOARD_FILE_VERSION = 1;
export const BOARD_FILE_EXTENSION = '.dvb';
/** The media type .dvb files are served with. */
export const BOARD_FILE_MIME = 'application/vnd.doriavboard+json';

const MAX_ELEMENTS = 50_000;

export interface BoardFileAsset {
  mime: string;
  width: number | null;
  height: number | null;
  /** Base64 file contents. */
  data: string;
}

export interface BoardFile {
  format: typeof BOARD_FILE_FORMAT;
  version: typeof BOARD_FILE_VERSION;
  title: string;
  exportedAt: string;
  frames: Frame[];
  /** Elements as stored, except stroke points, which are base64. */
  elements: Record<string, unknown>[];
  assets: Record<string, BoardFileAsset>;
}

export function serializeElement(el: BoardElement): Record<string, unknown> {
  if (el.type === 'stroke') return { ...el, points: bytesToBase64(el.points) };
  return { ...el };
}

export function deserializeElement(raw: unknown): BoardElement | null {
  if (typeof raw !== 'object' || raw === null) return null;
  const record = raw as Record<string, unknown>;
  if (record.type === 'stroke' && typeof record.points === 'string') {
    try {
      return sanitizeElement({ ...record, points: base64ToBytes(record.points) });
    } catch {
      return null;
    }
  }
  return sanitizeElement(record);
}

export class BoardFileError extends Error {}

/**
 * Validates an imported board file. Elements and frames are sanitized, elements
 * pointing at missing frames are dropped (as are frames and elements repeating
 * an id already seen), and a board always keeps at least one frame. Ids are
 * returned as found; callers should give them fresh ids.
 */
export function parseBoardFile(json: unknown): {
  title: string;
  content: BoardContent;
  assets: Record<string, BoardFileAsset>;
} {
  if (typeof json !== 'object' || json === null) throw new BoardFileError('Not a board file');
  const file = json as Partial<BoardFile>;
  if (file.format !== BOARD_FILE_FORMAT) {
    throw new BoardFileError('Not a DoriavBoard export file');
  }
  if (file.version !== BOARD_FILE_VERSION) {
    throw new BoardFileError(`Unsupported board file version ${String(file.version)}`);
  }
  if (!Array.isArray(file.frames) || !Array.isArray(file.elements)) {
    throw new BoardFileError('The board file is missing its frames or elements');
  }
  if (file.frames.length > MAX_FRAMES || file.elements.length > MAX_ELEMENTS) {
    throw new BoardFileError('The board file is too large to import');
  }

  const frameIds = new Set<string>();
  const frames = file.frames
    .map((f) => (typeof f === 'object' && f !== null ? normalizeFrame(f as never) : null))
    .filter((f): f is Frame => f !== null)
    .map((f) => ({ ...f, name: f.name.slice(0, 200), id: f.id.slice(0, 64) }))
    .filter((f) => !frameIds.has(f.id) && frameIds.add(f.id));
  if (frames.length === 0) {
    const frame = createFrame();
    frames.push(frame);
    frameIds.add(frame.id);
  }
  const elementIds = new Set<string>();
  const elements = file.elements
    .map(deserializeElement)
    .filter((el): el is BoardElement => el !== null && frameIds.has(el.frameId))
    .filter((el) => !elementIds.has(el.id) && elementIds.add(el.id));

  const assets: Record<string, BoardFileAsset> = {};
  if (typeof file.assets === 'object' && file.assets !== null) {
    for (const [id, asset] of Object.entries(file.assets)) {
      if (typeof asset?.data !== 'string' || typeof asset.mime !== 'string') continue;
      assets[id] = {
        mime: asset.mime,
        width: typeof asset.width === 'number' ? asset.width : null,
        height: typeof asset.height === 'number' ? asset.height : null,
        data: asset.data,
      };
    }
  }

  const title =
    typeof file.title === 'string' && file.title.trim() ? file.title.trim() : 'Imported board';
  return { title: title.slice(0, 120), content: { frames, elements }, assets };
}

/** Points image elements at new asset ids, dropping images whose asset is gone. */
export function remapAssetIds(
  elements: readonly BoardElement[],
  mapping: ReadonlyMap<string, string>,
): BoardElement[] {
  const out: BoardElement[] = [];
  for (const el of elements) {
    if (el.type !== 'image') out.push(el);
    else {
      const assetId = mapping.get(el.assetId);
      if (assetId) out.push({ ...el, assetId });
    }
  }
  return out;
}
